/**
 * The one flag that says a photo capture is in flight — set by
 * `usePhotoCapture.open()` the instant the OS sheet is asked for, cleared by
 * the input's `change` or `cancel` (final review's fix wave, D-040 postscript).
 *
 * It lives on `<html>` as `data-capturing` rather than in React state because
 * its only reader is outside React: `src/ui/appUpdate.ts`, deciding whether a
 * waiting build may reload the page. On iOS the camera and the photo picker
 * background the page (`visibilityState === 'hidden'`), which is exactly the
 * moment the deferred reload used to fire — under the picker, losing the
 * picture. A DOM attribute is readable from anywhere with no import cycle
 * (`appUpdate.ts` pulls in `virtual:pwa-register`, which the hook's unit
 * tests cannot load), and an e2e can set it from `page.evaluate` to prove
 * the gate without a real camera.
 */

const ATTRIBUTE = 'capturing';

function root(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.documentElement;
}

/** A capture has been asked for and not yet answered. */
export function markCaptureInFlight(): void {
  const element = root();
  if (element) element.dataset[ATTRIBUTE] = 'true';
}

/** The capture has been answered — a file landed, or the sheet was dismissed. */
export function clearCaptureInFlight(): void {
  const element = root();
  if (element) delete element.dataset[ATTRIBUTE];
}

export function isCaptureInFlight(): boolean {
  return root()?.dataset[ATTRIBUTE] === 'true';
}
